import { COMMON_SEARCH_PARAMETERS } from '@siliconlabs/utils/dist/constants';
import {
  ClientDocSpaceMenu,
  ClientDocLeafMenu,
  ClientSuperMenuItem,
  ClientMenuLink,
  ClientMenuExternalLink,
  ClientDocSpace,
  ClientDocLeaf,
  CollectionItem,
} from '../types';

/*
  Walks the menu and calls callback for each item
*/
const forEachInMenu = async (
  menu: ClientDocSpaceMenu | ClientDocLeafMenu,
  cb: any,
) => {
  const level = 1;

  const visitChildren = async (children, level) => {
    for (const child of children) {
      await cb(child, level);
      if (child.children) {
        await visitChildren(child.children, level++);
      }
    }
  };

  await visitChildren(menu.children, level);
};

/*
  Util to get the page of a string
*/
const getPage = (path?: string) => {
  if (path && typeof path === 'string') {
    const paths = path.split('/');
    return paths.length > 0 ? path.split('/')[paths.length - 1] : path;
  } else if (!path) {
    return '';
  } else {
    return path;
  }
};

const resolveIndexPage = (page: string, isStatic?: boolean) => {
  // If page is 'index', set to blank for browser, but index for static
  if (page === 'index' || !page) {
    return isStatic ? 'index' : '';
    // If page is defined and not index, return page
  } else {
    return page;
  }
};

/*
 * Resolves a relative URL path with a URL base (from root (/))
 */
const getFullyResolvedPathname = (rel: string, base: string) => {
  // new URL strips the anchors
  const url = new URL(rel, `http://${base}`);
  if (rel.includes('#')) {
    const anchor = rel.split('#')[1];
    return `${url.pathname}#${anchor}`;
  } else {
    return url.pathname;
  }
};

/*
  Search method to find the currently expanded path. Based on iterative DFS
  Returns undefined if no pathname match.
  Mutates menu and sets serverExpanded and partially adds parent nodes.
*/
const setMenuExpanded = (menu: ClientDocSpaceMenu) => {
  menu.url = '/';

  const stack = [menu as unknown as ClientSuperMenuItem];
  const visited = {};

  while (stack.length > 0) {
    let current = stack.pop();

    if (current?.url) {
      visited[current.url] = current;
    }

    // Found current menu item
    if (current?.active) {
      const ret: ClientSuperMenuItem[] = [];

      ret.push(current);
      current.serverExpanded = true;

      // Set all parents to expanded
      while (current.parent) {
        current = current.parent;

        ret.push(current);
        current.serverExpanded = true;
      }

      return ret;
    }

    // On each visit, set parents to all children and put in stack
    if (current?.children) {
      current.children.forEach((menuItem) => {
        menuItem.parent = current;
      });
      stack.push(...current.children);
    }
  }

  return undefined;
};

/*
  Walks the menu and adds active and path to nodes
*/
const decorateClientMenu = async (
  menu: ClientDocSpaceMenu | ClientDocLeafMenu,
  docSpace: ClientDocSpace,
  docLeaf: ClientDocLeaf,
  pathname: string,
  relPath?: string,
  isStatic?: boolean,
) => {
  // for storing active items, might be two
  const activeArray: Array<ClientMenuLink | ClientMenuExternalLink> = [];

  await forEachInMenu(
    menu,
    (menuItem: ClientMenuLink | ClientMenuExternalLink, level: number) => {
      let page = getPage(menuItem.url);
      page = resolveIndexPage(page, isStatic);

      // set menuItem level
      menuItem.level = level;

      if (menuItem.type === 'externalMenuLink') {
        menuItem.path = menuItem.url;
        return;
      }

      if (isStatic) {
        page = page.includes('#')
          ? `${page.split('#')[0]}.html#${page.split('#')[1]}`
          : `${page}.html`;
        if (docSpace) {
          menuItem.path = `${relPath}/../../${docSpace.id}/${docSpace.version}/${menuItem.docLeafId}/pages/${page}`;
        } else {
          menuItem.path = `${relPath}/pages/${page}`;
        }
      } else if (docSpace) {
        menuItem.path = getFullyResolvedPathname(
          `${relPath}/${docSpace.id}/${docSpace.version}/${menuItem.docLeafId}/${page || ''}`,
          pathname,
        );
      } else {
        menuItem.path = `/d/${docLeaf.id}/${docLeaf.version}/${page || ''}`;
      }

      // Add to active array if path matches
      const resolvedPath = getFullyResolvedPathname(menuItem.path, pathname);
      if (
        // Normalize by removing all trailing slashes in both paths
        pathname.replace(/\/+$/, '').endsWith(resolvedPath.replace(/\/+$/, ''))
      ) {
        activeArray.push(menuItem);
      }
    },
  );

  // Get deepest active menu item
  activeArray.sort((a, b) => (a.level && b.level ? a.level - b.level : 0));
  const activeItem = activeArray[activeArray.length - 1];

  if (activeItem) {
    activeItem.active = true;
  }
};

/* This functions converts the tagSet to clean array strings keywords for searching and rendering */
const getFilterListFromTags = (
  tagSet: any,
  docSpace?: ClientDocSpace,
  docLeaf?: ClientDocLeaf,
): string[] => {
  if (
    tagSet?.tags?.['docs-hierarchy'] &&
    typeof tagSet.tags['docs-hierarchy'] === 'string'
  ) {
    const filters = tagSet.tags['docs-hierarchy'].split(',');
    const lastFilter = filters[filters.length - 1];
    const filterList = lastFilter.split('|');

    if (filterList.length && docSpace?.version) {
      filterList[filterList.length - 1] = docSpace.version;
    } else if (filterList.length && !docSpace && docLeaf?.version) {
      filterList[filterList.length - 1] = docLeaf.version;
    } else {
      // return root filter
      return [filters[0]];
    }
    return filterList;
  }
  // there are no docs-hierarchy metadata, don't show filter bar, or show just root
  return ['Documents'];
};

/* This function handles a search submit */
const externalSearchRedirect = (value, filters) => {
  let searchUrl = `/search#q=${value.trim()}`;

  Object.keys(COMMON_SEARCH_PARAMETERS).forEach((key) => {
    searchUrl += `&${key}=${COMMON_SEARCH_PARAMETERS[key]}`;
  });

  searchUrl += `&f:@common_docs_hierarchy=[${filters}]`;
  window.location.replace(searchUrl);
};

const flattenItems = (items: CollectionItem[]) => {
  const handleLatestVersion = (currentVer: string, versions: string[]) => {
    const latestSemver = versions
      .find((ver) => ver.endsWith(' (latest)'))
      ?.split('/')[2];
    let ret = currentVer;

    if (currentVer.endsWith('/latest') && latestSemver) {
      ret = currentVer.replace('latest', latestSemver);
    }

    if (latestSemver?.includes(currentVer.split('/')[1])) {
      ret = currentVer.replace(currentVer.split('/')[1], latestSemver);
    }

    return ret;
  };
  const recurse = (item: CollectionItem, i: number): CollectionItem[] => {
    const result: CollectionItem[] = [];
    item.content = handleLatestVersion(item.content, item.versions || []);

    result.push(item);
    if (item.children) {
      const children = item.children.map((child, index) => {
        child.nestedPosition = `${item.nestedPosition ? item.nestedPosition : i + 1}.${child.position ?? index + 1}`;
        child.content = handleLatestVersion(
          child.content,
          child.versions || [],
        );
        return child;
      });

      for (const child of children) {
        if (child.type === 'group') {
          result.push(...recurse(child, 0));
        }
      }
    }

    return result;
  };

  return items.flatMap((item, index) => recurse(item, index));
};

export {
  setMenuExpanded,
  decorateClientMenu,
  forEachInMenu,
  getFilterListFromTags,
  externalSearchRedirect,
  flattenItems,
};
